// The scope lives in navigation.ts, where all eight of this console's cross-boundary scopes are
// collected so "what can this console reach" is answerable from one file. Re-exported so a reader
// of this module finds it, and so channel-scopes.mjs sees one literal rather than two.
export { EVENT_SCHEMAS_SCOPE } from "./navigation.ts";

import type { PlatformSchemaSummary } from "@tdelivr/api-client/generated/event-platform/event-schema-registry.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the event schema registry board does to the summaries EVT-01 returns, away from any
 * rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `schemas`, against EVT-01
 * `GET /v1/platform/event-schemas` — published by
 * [ADR 0057](../../../docs/adr/0057-a-platform-scoped-event-schema-listing-is-a-separate-operation-and-the-schema-document-stays-inside.md)
 * because this service published **no listing of any kind**, internally or publicly: a
 * registration, a by-version read and a compatibility check, and the by-version read needs the
 * `subject` and `version` a board exists to discover.
 *
 * ```
 * schemas:['Event schema registry','EVT-01','Published contracts are immutable. A change is a new
 *   version; the old one stays readable forever.',
 *   [['Schemas','318','done','published'],['Drafts','4','idle','unpublished'],
 *    ['Breaking changes blocked','1','failed','this week'],['Consumers','96','done','subscribed']],
 *   ['Schema','Version','Compatibility'],
 *   [['payments.settled','v2','backward','lock','immutable'],
 *    ['delivery.exception.opened','v3','backward','lock','immutable'],
 *    ['trip.state.changed','v5 draft','breaking','failed','blocked: breaking'],
 *    ['ride.offer.expired','v1','full','lock','immutable']],
 *   'lock','Published rows show a lock; there is no edit button on a published schema.']
 * ```
 *
 * ## All three columns have a source, and the listing's seven fields fill them exactly
 *
 * `Schema` is `subject`, `Version` is `version` and `Compatibility` is `compatibilityPolicy`. That
 * is unusual on this console — the other four boards each draw at least one column another service
 * owns — and it is why ADR 0057 argues its projection rather than its route: what this read
 * withholds is the schema document, which the page draws no part of, so nothing the page draws is
 * withheld.
 *
 * ## One row per **version**, not one per subject, and the design's sample hides the difference
 *
 * The listing answers every version of every subject, ordered by `(subject, version)` — the primary
 * key's own column order — so `payments.settled` v1 and v2 are two rows with the same first cell,
 * adjacent. The design draws four rows with four distinct subjects, which reads as one row per
 * subject showing its latest version, and nothing in this service supports that reading: there is
 * no "latest" flag, `latest(subject)` is the workload read's own query, and the listing has no
 * filter for it (ADR 0057 §4 — `limit` and `after` are the only two things a caller may say).
 *
 * Collapsing to the newest version per subject was therefore rejected twice over: it would hide
 * the rows the page's own sentence is about — "the old one stays readable forever" — and on a
 * truncated answer it would collapse across a page boundary, calling a version latest because it
 * happened to be last on the page. `versionSpanNote` says what the board is a list of instead, and
 * **Q-200** is where the design's row grammar is recorded.
 *
 * ## The badge is a constant, and that is the record's promise rather than a missing column
 *
 * Three of the design's four rows are badged `lock · immutable`, and nothing is read to decide it:
 * every row of `event_schemas` is immutable **by trigger** — `0002_enforce_immutability.sql` raises
 * on any UPDATE or DELETE, for the owning role as well — so the badge comes from the fact rather
 * than from a field. ADR 0057's "what this does not do" says so in those terms.
 *
 * **It is drawn `closed` rather than `lock`, which is a recorded divergence rather than a choice.**
 * `lock` is a **tenth** state class that `16 WP15 Platform Machinery.dc.html` declares in its `SEMK`
 * map — along with six other work-package files — and that `01 Token System v2.dc.html` does not.
 * `@tdelivr/design-tokens`' `StateClassKey` is generated from the token file by
 * `tooling/scripts/extract-state-classes.mjs` and carries nine, so `lock` is not a class this
 * console can name. `closed` is the nearest, and nearest in the design's own terms: `SEMK.lock` and
 * `SEMK.closed` draw the **same `square` glyph** and differ only in tint —
 * `var(--accentTint)` with `#3E63DD` against `var(--muted)` with `var(--text2)`. So the shape and
 * the word are the design's and the tint is not, which is a visible difference and is **Q-193**,
 * already recorded against the flags board's `armed` badge. The page's own closing note —
 * "Published rows show a lock" — is the sentence that difference is measured against.
 *
 * ## What has no source
 *
 * **The design's third row, whole.** `trip.state.changed · v5 draft · breaking · failed ·
 * blocked: breaking` is a candidate schema that was never registered, and a registration either
 * inserts an immutable row or throws 409. The compatibility check stores nothing. So there is no
 * `draft` version, no `breaking` policy — `compatibilityPolicy` is
 * `backward | forward | full`, and the form's fourth option `none` is not modelled either — and no
 * `failed` badge. **Q-200**, and ADR 0057 refuses to invent the state: a draft row in
 * `event_schemas` would be a second kind of row behind the primary key whose whole promise is that
 * a published version never changes.
 *
 * **All four stat tiles.** `Schemas · 318` is a count, and R-04's measurement is that no collection
 * read on this platform publishes a total, so a tile reading `4` would mean "4 so far" under a
 * label meaning "318". `Drafts · 4` and `Breaking changes blocked · 1` count the rows the paragraph
 * above says do not exist, and `Consumers · 96 · subscribed` is a fourth absence of a different
 * kind: **nothing on this platform models a schema's consumers**. Q-200 carries the three, and
 * Q-178 carries the tile row across this console.
 *
 * **`Last changed`, one of the design's three `History` lines.** There is no second instant and
 * there cannot be one: the row is immutable by trigger, so EVT-01 holds no `updated_at` to publish.
 * It is drawn as the design's own `—` rather than filled with the registration instant under a
 * label meaning something else, and rather than dropped. The actor beside the design's first two
 * lines — `12 Aug 2026 · W. Mutua` — is Q-107's, as on every other board: who registered a contract
 * is AUD-01's question, answered there under `audit:platform:read`, and ADR 0057 §4 declines to
 * duplicate it behind a second scope.
 *
 * **The schema document, deliberately, and this board does not want it.** It carries
 * `sensitive: boolean` per property, so across the registry it is a map of which fields of which
 * events on this platform hold personal data — and the page draws no property name at all. It is
 * answered only by `GET /internal/v1/subjects/{subject}/versions/{version}` under
 * `event-schema:read`. ADR 0057 is mostly about that sentence. The `digest` published in its place
 * is the document's identity rather than the document, and is what an operator comparing two
 * environments has to compare.
 */

/**
 * The scope EVT-01's platform listing is published under.
 *
 * Written here rather than imported from `navigation.ts`, which does not yet export one: that file
 * still records this page as blocked and is wired centrally. It is a **string literal in this
 * application's own source**, which is what matters for the grant —
 * `tooling/scripts/lib/channel-scopes.mjs` derives each channel's Keycloak client from the literals
 * it finds in that application's TypeScript AST, intersected with the published scope vocabulary —
 * so naming it here is what makes `tdelivr-platform-console` hold it (ADR 0032, ADR 0049 §3). It
 * belongs beside the other four once the page is routed.
 *
 * **Q-139** records that nobody has decided who may hold a scope that reads across the platform,
 * and this one reads every event contract's identity. ADR 0057's consequences say the same.
 */

/**
 * The design's `Version` cell: `v2`, `v3`, `v1`.
 *
 * Its own `v` prefix and the published integer, which is assigned by the service —
 * `(previous?.version ?? 0) + 1` — and is half of the listing's cursor. The design's fourth
 * grammar, `v5 draft`, is unreachable: see this file's header and Q-200.
 */
export function versionText(summary: PlatformSchemaSummary): string {
  return `v${String(summary.version)}`;
}

/**
 * The state class and the word, which are the same on every row of this board.
 *
 * `immutable` is the design's own word and `closed` is the nearest of the token system's nine to its
 * `lock` — see this file's header, and Q-193. The `title` says why the badge needs no field, because
 * an administrator reading a board where every badge is identical is entitled to know whether that
 * is the data or the record.
 *
 * A state is never shown by colour alone: the badge carries a `square` glyph and the word
 * `immutable`, which is what `StateBadge` draws from `classKey`.
 */
export function stateOf(): { classKey: StateClassKey; label: string; title: string } {
  return {
    // Q-193: the design's own class here is `lock`, which the token system does not carry.
    classKey: "closed",
    label: "immutable",
    title:
      "Every row of this registry is immutable by trigger — an UPDATE or a DELETE raises, for the owning role as well — so this badge comes from the record's own promise rather than from a published field. A change is a new version.",
  };
}

/**
 * One row of the event schema registry board.
 *
 * The detail panel carries the three fields the grid's columns cannot — the owning service, the
 * event type and the digest — because they are published, because the design's panel is where the
 * fields a row's columns could not carry go, and because "which service owns this contract, and is
 * this the same document as in staging" are the two questions a platform operator opens this row to
 * ask. The design takes the panel's glyph from the page's own form icon, `schema`.
 */
export function schemaRow(summary: PlatformSchemaSummary): BoardRow {
  return {
    /*
     * `subject:version`, which is EVT-01's primary key and this listing's own cursor.
     *
     * The subject alone would collide: the listing answers every version of a subject, so two rows
     * of `payments.settled` would share a React key and one would replace the other on a board whose
     * own page sentence is that the old version stays readable forever.
     */
    id: `${summary.subject}:${String(summary.version)}`,
    cells: [
      /*
       * Not mono, which is the design's own rule rather than an oversight.
       *
       * Its grid cell takes Geist Mono only for a value matching its prefix list — `ten_`, `evt_`,
       * `RPL`, `RSK`, `wf_`, `/v1`, `key`, `offset` — and `payments.settled` matches none of them,
       * so the design draws a subject in Geist at 600. The digest in the panel below is a code and
       * is mono there.
       */
      { text: summary.subject },
      { text: versionText(summary) },
      {
        text: summary.compatibilityPolicy,
        title:
          "The policy this version was registered under, which EVT-01 checks the candidate against before it inserts anything. backward, forward or full are the three it models; the design's fourth cell, breaking, is a refused registration and leaves no row. Q-200.",
      },
    ],
    state: stateOf(),
    // The design tints a row for its `human` and `failed` classes only, and neither is reachable
    // here: a registry row that exists is a registration that succeeded.
    detail: [
      {
        label: "Owner service",
        value: summary.ownerServiceId,
        mono: true,
      },
      { label: "Event type", value: summary.eventType },
      {
        /*
         * The canonical SHA-256 of the document, in full and in Geist Mono.
         *
         * In full because a shortened digest is a different value: two environments agreeing on
         * eight characters is not two environments holding the same contract, and comparing them is
         * the one thing this field is published for. The design's panel sets
         * `overflow-wrap: anywhere` on its values, so a 64-character code wraps rather than
         * overflowing. Mono because `CLAUDE.md` section 3.2 gives Geist Mono to identifiers and
         * codes, which is the rule the design's own prefix list is an instance of.
         */
        label: "Digest",
        value: summary.digest,
        mono: true,
      },
    ],
    /*
     * The design's `History` block, with one of its three lines drawn as its own `—`.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. Here the registration instant is the
     * whole history a contract has: there is no second instant, because the row cannot change
     * (`migrations/0002_enforce_immutability.sql`), and no actor, because who registered a contract
     * is AUD-01's record rather than this one's. So `Created` carries the instant without the name
     * — Q-107, as on every other board — and `Last changed` carries the absence rather than a
     * repeat of the first line under a label meaning something else. This file's header records
     * both.
     */
    history: [
      { label: "Created", value: asDay(summary.registeredAt) },
      { label: "Last changed", value: "", absent: true },
      { label: "Version", value: versionText(summary) },
    ],
    // The design takes the detail panel's glyph from the page's own form icon, which here is
    // `schema` — `FORMS.schemas[2]`.
    glyph: "schema",
  };
}

/**
 * The states this listing can answer, which is one.
 *
 * The design's second badge, `failed · blocked: breaking`, needs the refused registration Q-200
 * records as leaving no row, so the filter offers the one state the board can be in. Its options are
 * derived rather than written out for the same reason every other board's are: a filter offering a
 * state no row can be in is a filter that empties the board and tells the administrator nothing.
 */
export const SCHEMA_STATES: readonly string[] = ["immutable"];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(summaries: readonly PlatformSchemaSummary[]): readonly string[] {
  return summaries.length === 0 ? [] : SCHEMA_STATES;
}

/**
 * What the board owes an operator about what one of its rows is.
 *
 * Said rather than implied, because the design's four rows are four distinct subjects and this board
 * draws one row per **version** — see this file's header. An operator who reads the grid as one row
 * per contract will miscount the registry by every superseded version it holds, and the sentence is
 * cheaper than the misreading. `partner-api.ts`'s `tenantSpanNote` and `platform-flags.ts`'s
 * `flagScopeNote` are the same sentence in the same position for the same reason.
 *
 * The subject count is of the rows on screen and is said as such. It is not a total: R-04, and this
 * listing's `nextAfter` is what `boundedNote` says the rest with.
 */
export function versionSpanNote(
  summaries: readonly PlatformSchemaSummary[] | undefined,
): string | undefined {
  if (summaries === undefined || summaries.length === 0) return undefined;
  const subjects = new Set(summaries.map((summary) => summary.subject)).size;
  return `${String(summaries.length)} registered version${summaries.length === 1 ? "" : "s"} of ${String(subjects)} subject${subjects === 1 ? "" : "s"}. A row is one version, not one contract: a published version is never replaced, so every version a subject has ever had is listed, ordered by subject and then by version.`;
}

/**
 * Why the designed `Register version` form is drawn and refused.
 *
 * Its fields are `Event name`, `Version`, `Compatibility`, `JSON Schema` and `Change notes`, and its
 * primary is `Validate and register`. Four reasons, and any one of them is enough:
 *
 *   - **The registration is `/internal/v1` only** —
 *     `POST /internal/v1/subjects/{subject}/versions` under `event-schema:publish` — which the
 *     gateway does not expose (ADR 0029). ADR 0057 §6 publishes a read and nothing else, and says
 *     why: a registration is a governed command carrying an idempotency key, writing an audit
 *     record and an outbox event, and it does not belong on a board's origin.
 *   - **`Version` has nothing on the wire.** EVT-01 assigns it —
 *     `(previous?.version ?? 0) + 1` — so the form's `v6` is a value the service would ignore and
 *     the field is a promise it cannot keep.
 *   - **`Compatibility` offers `none`**, which `compatibilityPolicy` does not model: the enum is
 *     `backward | forward | full` in the domain, the route's Zod schema and the published contract
 *     alike. Q-200.
 *   - **`Change notes` has nothing on the wire either.** The registration body is `.strict()` over
 *     `ownerServiceId`, `eventType`, `compatibilityPolicy` and `schema`, so a note would be refused
 *     as an unknown property rather than stored.
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` section 1 says the add or edit form is
 * not to be removed, and rather than wired to something that would answer 404.
 */
export const REGISTER_REFUSAL =
  "EVT-01 publishes the registration on /internal/v1 only, which the gateway does not expose, and the form's Version is assigned by the service, its Compatibility offers a policy the contract does not model, and its Change notes has no field on the wire. ADR 0057 §6, Q-200.";
